import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import reportsService from '../services/firebase/reportsService';

const BRAND_COLOR = [59, 130, 246]; // Primary Blue (#3b82f6)
const DARK_COLOR = [30, 41, 59]; // Text Dark (#1e293b)

const generateHeader = (doc, title, filters) => {
    // Add Logo or Title
    doc.setFontSize(22);
    doc.setTextColor(...BRAND_COLOR);
    doc.setFont("helvetica", "bold");
    doc.text("DigiSahyadri HRM", 14, 20);

    // Add Report Title
    doc.setFontSize(16);
    doc.setTextColor(...DARK_COLOR);
    doc.text(title, 14, 30);

    // Add Filter Meta Data
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "normal");
    
    let metaText = `Generated on: ${moment().format('DD MMM YYYY, hh:mm A')}`;
    if (filters.month) {
        metaText += ` | Month: ${moment(filters.month, 'YYYY-MM').format('MMMM YYYY')}`;
    }
    if (filters.department) {
        metaText += ` | Department: ${filters.department}`;
    }
    
    doc.text(metaText, 14, 38);
    
    // Horizontal Line
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(14, 42, 196, 42);
    
    return 48; // Return starting Y position for table
};

const getAttendanceTableData = (records) => {
    const head = [['Emp Code', 'Name', 'Department', 'Working Days', 'Present', 'Absent', 'Late', 'Half Day', 'Attendance %']];
    const body = records.map(r => [
        r.employeeId || 'N/A',
        r.employeeName || 'Unknown',
        r.department || 'N/A',
        r.totalWorkingDays || 0,
        r.presentDays || 0,
        r.absentDays || 0,
        r.lateMarks || 0,
        r.halfDays || 0,
        `${r.attendancePercentage || 0}%`
    ]);
    return { head, body };
};

const getLeaveTableData = (records) => {
    const head = [['Emp Code', 'Name', 'Department', 'Total Allowed', 'Used', 'Pending', 'Remaining']];
    const body = records.map(r => [
        r.employeeId || 'N/A',
        r.employeeName || 'Unknown',
        r.department || 'N/A',
        r.totalAllowed || 0,
        r.used || 0,
        r.pending || 0,
        r.remaining || 0
    ]);
    return { head, body };
};

const getMovementTableData = (records) => {
    const head = [['Emp Code', 'Name', 'Department', 'Type', 'Date', 'Status']];
    const body = records.map(r => [
        r.employeeId || 'N/A',
        r.employeeName || 'Unknown',
        r.department || 'N/A',
        r.type === 'Joining' ? 'Joining' : 'Exit',
        moment(r.date).format('DD MMM YYYY'),
        r.status || 'Completed'
    ]);
    return { head, body };
};

const getDepartmentTableData = (records) => {
    const head = [['Dept Code', 'Department Name', 'Total Emps', 'Working Days', 'Present', 'Absent', 'Attendance %']];
    const body = records.map(r => [
        r.departmentCode || 'N/A',
        r.departmentName || 'Unknown',
        r.totalEmployees || 0,
        r.totalWorkingDays || 0,
        r.presentDays || 0,
        r.absentDays || 0,
        `${r.attendancePercentage || 0}%`
    ]);
    return { head, body };
};

export const exportSingleReportPDF = (reportType, records, filters) => {
    const doc = new jsPDF();
    let title = "HRM Report";
    let tableData = { head: [], body: [] };

    switch (reportType) {
        case 'attendance':
            title = "Monthly Attendance Report";
            tableData = getAttendanceTableData(records);
            break;
        case 'leave':
            title = "Leave Summary Report";
            tableData = getLeaveTableData(records);
            break;
        case 'employee-movement':
            title = "Employee Joining/Exit Report";
            tableData = getMovementTableData(records);
            break;
        case 'department-wise':
            title = "Department-wise Attendance Report";
            tableData = getDepartmentTableData(records);
            break;
    }

    const startY = generateHeader(doc, title, filters);

    autoTable(doc, {
        startY,
        head: tableData.head,
        body: tableData.body,
        theme: 'striped',
        headStyles: { fillColor: BRAND_COLOR },
        styles: { fontSize: 8 },
        margin: { top: 14, left: 14, right: 14 },
    });

    const filename = `${title.replace(/ /g, '_')}_${moment().format('YYYYMMDD')}.pdf`;
    doc.save(filename);
};

export const generateMasterReportPDF = async (filters, user) => {
    // 1. Fetch all reports data
    const fetchWithFilters = (method) => {
        const queryParams = { ...filters };
        if (user && (!user.role || user.role === 'employee')) {
            queryParams.employeeId = user.employeeCode || user.uid;
            queryParams.employeeCode = user.employeeCode;
            queryParams.employeeUid = user.uid;
        }
        return method(queryParams);
    };

    const [attendanceRes, leaveRes, movementRes, deptRes] = await Promise.all([
        fetchWithFilters(reportsService.getAttendanceReport),
        fetchWithFilters(reportsService.getLeaveReport),
        fetchWithFilters(reportsService.getEmployeeMovementReport),
        fetchWithFilters(reportsService.getDepartmentWiseReport)
    ]);

    const attendanceData = attendanceRes?.data || [];
    const leaveData = leaveRes?.data || [];
    const movementData = movementRes?.data || [];
    const deptData = deptRes?.data || [];

    // 2. Initialize PDF
    const doc = new jsPDF();
    const startY = generateHeader(doc, "Master HRM Analytics Report", filters);
    let currentY = startY;

    const addSection = (title, dataGenerator, records) => {
        if (records.length === 0) return;
        
        if (currentY > 250) {
            doc.addPage();
            currentY = 20;
        }

        // Section Title
        doc.setFontSize(14);
        doc.setTextColor(...DARK_COLOR);
        doc.setFont("helvetica", "bold");
        doc.text(title, 14, currentY);
        currentY += 6;

        const tableData = dataGenerator(records);
        autoTable(doc, {
            startY: currentY,
            head: tableData.head,
            body: tableData.body,
            theme: 'striped',
            headStyles: { fillColor: BRAND_COLOR },
            styles: { fontSize: 8 },
            margin: { left: 14, right: 14 },
            didDrawPage: (data) => {
                currentY = data.cursor.y + 15;
            }
        });
    };

    addSection("1. Monthly Attendance", getAttendanceTableData, attendanceData);
    addSection("2. Leave Summary", getLeaveTableData, leaveData);
    addSection("3. Employee Joining/Exit", getMovementTableData, movementData);
    addSection("4. Department-wise Attendance", getDepartmentTableData, deptData);

    const filename = `HRM_Master_Report_${moment().format('YYYYMMDD_HHmm')}.pdf`;
    doc.save(filename);
};
